import type L from 'leaflet';
import { useCallback, useRef, useState } from 'react';
import { useSelector } from 'react-redux';

import type { RootState } from '../../../../../store/store';
import { JWTAuthorization, JWTHeader } from '../../../../../utils/JWTHeader';
import { lambert93ToWGS84 } from '../../../../../utils/mapUtils';
import { showToast } from '../../../../common/Notification';
import type { Address, Parcelle, SelectedCopro } from '../../types';
import type { AutocompleteFeature } from '../BanSearch';

const GOOGLE_API_KEY = process.env.REACT_APP_GOOGLE_API_KEY;

interface BanAddressApiResponse {
  idAdr: string;
  geometry: string;
  numero: string;
  indiceRepetition?: string;
  nomVoie: string;
  codePostal: string;
  commune: string;
}

interface CadastreParcelleApiResponse {
  id: string;
  geometry: string;
}

interface UseAddressQualifierResult {
  // State
  parcelles: Parcelle[];
  addresses: Address[];
  manualAddresses: Address[];
  mapCenter: [number, number];
  mapZoom: number;
  addressFound: boolean;
  shouldUpdateView: boolean;
  isLoading: boolean;
  isSaving: boolean;
  searchQuery: string;
  showManualAddressInput: boolean;
  lastAutocompleteResult: AutocompleteFeature | null;
  isConfirmationOpen: boolean;
  showLastResultPopup: boolean;
  mapRef: React.MutableRefObject<L.Map | null>;

  // Setters
  setShouldUpdateView: (value: boolean) => void;
  setSearchQuery: (query: string) => void;
  setShowManualAddressInput: (show: boolean) => void;
  setIsConfirmationOpen: (open: boolean) => void;
  setShowLastResultPopup: (show: boolean) => void;

  // Actions
  handleMapClick: (lat: number, lng: number) => Promise<void>;
  handleParcelleClick: (parcelleId: string, event: L.LeafletMouseEvent) => Promise<void>;
  handleAddressCheck: (addressId: string) => void;
  handleSelectAll: () => void;
  handleDeselectAll: () => void;
  handleAddressClick: (geometry: string, convertFromLambert?: boolean) => void;
  handleSearchSelect: (result: AutocompleteFeature) => void;
  handleAddManualAddress: (result: AutocompleteFeature) => void;
  handleDeleteManualAddress: (addressId: string) => void;
  handleConfirmSave: (force?: boolean) => void;
  handleSave: () => Promise<void>;
  handleAddLastResult: () => void;
  handleUseInSearch: (address: string) => void;
  flyToLocation: (lat: number, lng: number, zoom?: number) => void;
  initializeCopro: (copro: SelectedCopro) => void;
  resetState: () => void;
}

function parseGeometry(geometry: string, convertFromLambert = true): { lat: number; lng: number } | null {
  const match = geometry.match(/POINT\(([-\d.]+) ([-\d.]+)\)/);
  if (match) {
    const x = parseFloat(match[1]);
    const y = parseFloat(match[2]);
    if (!convertFromLambert) {
      return { lat: y, lng: x };
    }
    return lambert93ToWGS84(x, y);
  }
  return null;
}

export function useAddressQualifier(
  selectedCopro: SelectedCopro | null,
  onSuccess: (updatedCopro?: SelectedCopro) => void,
): UseAddressQualifierResult {
  const jwt = useSelector((state: RootState) => state.auth.token);

  const [parcelles, setParcelles] = useState<Parcelle[]>([]);
  const [addresses, setAddresses] = useState<Address[]>([]);
  const [manualAddresses, setManualAddresses] = useState<Address[]>([]);
  const [mapCenter, setMapCenter] = useState<[number, number]>([46.603354, 1.888334]);
  const [mapZoom, setMapZoom] = useState(6);
  const [addressFound, setAddressFound] = useState(false);
  const [shouldUpdateView, setShouldUpdateView] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [showManualAddressInput, setShowManualAddressInput] = useState(false);
  const [isConfirmationOpen, setIsConfirmationOpen] = useState(false);
  const [lastAutocompleteResult, setLastAutocompleteResult] = useState<AutocompleteFeature | null>(null);
  const [showLastResultPopup, setShowLastResultPopup] = useState(false);

  const mapRef = useRef<L.Map | null>(null);
  const hasAutoAddedRef = useRef(false);
  const currentCoproIdRef = useRef<number | null>(null);

  const resetState = useCallback(() => {
    setParcelles([]);
    setAddresses([]);
    setManualAddresses([]);
    setMapCenter([46.603354, 1.888334]);
    setMapZoom(6);
    setAddressFound(false);
    setShouldUpdateView(true);
    setIsLoading(false);
    setSearchQuery('');
    setShowManualAddressInput(false);
    setLastAutocompleteResult(null);
    hasAutoAddedRef.current = false;
  }, []);

  const flyToLocation = useCallback((lat: number, lng: number, zoom = 19) => {
    if (mapRef.current) {
      mapRef.current.flyTo([lat, lng], zoom, {
        animate: true,
        duration: 0.75,
      });
    }
    setMapCenter([lat, lng]);
    setMapZoom(zoom);
    setShouldUpdateView(true);
  }, []);

  const fetchAddressLocation = useCallback(async(remoteId: string) => {
    try {
      const response = await fetch(`/api/ban/${remoteId}`, JWTHeader(jwt));
      if (!response.ok) { throw new Error('Failed to fetch address location'); }
      return await response.json();
    } catch (error) {
      console.error('Error fetching address location:', error);
      return null;
    }
  }, [jwt]);

  const fetchAddresses = useCallback(async(parcelleId: string): Promise<Address[]> => {
    try {
      const response = await fetch(`/api/ban/plots/${parcelleId}/addresses`, JWTHeader(jwt));
      const addressesData = await response.json();
      const newAddresses = addressesData.map((addr: BanAddressApiResponse) => {
        const pointAddress = JSON.parse(addr.geometry);

        return {
          id: addr.idAdr,
          address: `${addr.numero + (addr.indiceRepetition ? addr.indiceRepetition : '')} ${addr.nomVoie}`,
          postalCode: addr.codePostal,
          city: addr.commune,
          selected: true,
          parcelleId,
          geometry: pointAddress ? `POINT(${pointAddress.coordinates[0]} ${pointAddress.coordinates[1]})` : null,
        };
      });
      setAddresses((prev) => [...prev, ...newAddresses]);
      return newAddresses;
    } catch (error) {
      console.error('Error fetching addresses:', error);
      return [];
    }
  }, [jwt]);

  const addParcelle = useCallback(async(lat: number, lng: number) => {
    setIsLoading(true);
    try {
      const response = await fetch(`/api/cadastre/plots/geolocation/${lng}/${lat}`, JWTHeader(jwt));
      if (!response.ok) { throw new Error('Failed to fetch parcelle'); }
      const data = await response.json();

      if (data && data.length > 0) {
        const formattedParcelles = data.map((parcelle: CadastreParcelleApiResponse) => ({
          id: parcelle.id,
          geometry: JSON.parse(parcelle.geometry),
        }));

        setParcelles((prev) => [...prev, ...formattedParcelles]);
        await fetchAddresses(formattedParcelles[0].id);
      }
    } catch (error) {
      console.error('Error fetching parcelles:', error);
      showToast('error', 'Une erreur est survenue lors du chargement de la parcelle.');
    } finally {
      setIsLoading(false);
    }
  }, [jwt, fetchAddresses]);

  const initializeCopro = useCallback(async(copro: SelectedCopro) => {
    if (currentCoproIdRef.current === copro.id) { return; }

    resetState();
    currentCoproIdRef.current = copro.id;

    let autoAddedParcelle = false;

    // Fetch address from Google Maps and fly to it
    const rawAddress = copro.details?.adresseDeReference || '';
    if (rawAddress && GOOGLE_API_KEY) {
      try {
        const response = await fetch(
          `https://maps.googleapis.com/maps/api/geocode/json?address=${encodeURIComponent(rawAddress)}&key=${GOOGLE_API_KEY}`,
        );
        const data = await response.json();

        if (data.results && data.results.length > 0) {
          const result = data.results[0];
          const { lat, lng } = result.geometry.location;
          setAddressFound(true);
          flyToLocation(lat, lng, 19);

          // Auto-add parcelle if no existing plots
          if (copro.plots.length === 0) {
            hasAutoAddedRef.current = true;
            autoAddedParcelle = true;
            await addParcelle(lat, lng);
          }
        } else {
          setAddressFound(false);
        }
      } catch (error) {
        console.error('Error fetching address:', error);
        setAddressFound(false);
      }
    }

    // Fetch existing parcelles if any (only if we have plots to fetch)
    // Skip if we already auto-added a parcelle and there are no existing plots
    if (copro.plots.length > 0) {
      setIsLoading(true);
      try {
        // Fetch all parcelles in parallel
        const parcellePromises = copro.plots.map((plot) => fetch(`/api/cadastre/plots/${plot.plot}`, JWTHeader(jwt))
          .then((response) => {
            if (!response.ok) { throw new Error('Failed to fetch parcelle'); }
            return response.json();
          }));

        const parcellesData = await Promise.all(parcellePromises);
        const formattedParcelles = parcellesData.map((parcelle: CadastreParcelleApiResponse) => ({
          id: parcelle.id,
          geometry: JSON.parse(parcelle.geometry),
        }));

        setParcelles(formattedParcelles);

        // Fetch addresses for all parcelles in parallel
        const addressPromises = formattedParcelles.map((parcelle) => fetch(`/api/ban/plots/${parcelle.id}/addresses`, JWTHeader(jwt))
          .then((response) => response.json())
          .then((addressesData) => addressesData.map((addr: BanAddressApiResponse) => {
            const pointAddress = JSON.parse(addr.geometry);
            const isAddressInExisting = copro.addresses.some(
              (existingAddr) => existingAddr.remoteId === addr.idAdr,
            );

            return {
              id: addr.idAdr,
              address: `${addr.numero + (addr.indiceRepetition ? addr.indiceRepetition : '')} ${addr.nomVoie}`,
              postalCode: addr.codePostal,
              city: addr.commune,
              selected: isAddressInExisting,
              parcelleId: parcelle.id,
              geometry: pointAddress ? `POINT(${pointAddress.coordinates[0]} ${pointAddress.coordinates[1]})` : null,
            };
          })));

        const allParcelleAddresses = await Promise.all(addressPromises);
        const flatAddresses = allParcelleAddresses.flat();
        setAddresses(flatAddresses);

        // Handle addresses not found in parcelles - fetch all locations in parallel
        const fetchedAddressIds = flatAddresses.map((a) => a.id);
        const missingAddresses = copro.addresses.filter((addr) => !fetchedAddressIds.includes(addr.remoteId));

        const manualAddressPromises = missingAddresses.map(async(addr) => {
          const locationData = await fetchAddressLocation(addr.remoteId);
          if (locationData) {
            const locationParsed = JSON.parse(locationData.geometry);
            const stdCoordinates = locationParsed ? lambert93ToWGS84(locationParsed.coordinates[0], locationParsed.coordinates[1]) : null;
            return {
              id: addr.remoteId,
              address: addr.address,
              postalCode: addr.postalCode,
              city: addr.city,
              selected: true,
              geometry: stdCoordinates ? `POINT(${stdCoordinates.lng} ${stdCoordinates.lat})` : null,
            } as Address;
          }
          return null;
        });

        const manualAddressResults = await Promise.all(manualAddressPromises);
        const validManualAddresses = manualAddressResults.filter((addr): addr is Address => addr !== null);
        setManualAddresses(validManualAddresses);
      } catch (error) {
        console.error('Error fetching existing data:', error);
        showToast('error', 'Une erreur est survenue lors du chargement des données existantes.');
      } finally {
        setIsLoading(false);
      }
    } else if (copro.addresses.length > 0 && !autoAddedParcelle) {
      // Handle case where there are addresses but no plots (shouldn't normally happen, but handle gracefully)
      setIsLoading(true);
      try {
        const manualAddressPromises = copro.addresses.map(async(addr) => {
          const locationData = await fetchAddressLocation(addr.remoteId);
          if (locationData) {
            const locationParsed = JSON.parse(locationData.geometry);
            const stdCoordinates = locationParsed ? lambert93ToWGS84(locationParsed.coordinates[0], locationParsed.coordinates[1]) : null;
            return {
              id: addr.remoteId,
              address: addr.address,
              postalCode: addr.postalCode,
              city: addr.city,
              selected: true,
              geometry: stdCoordinates ? `POINT(${stdCoordinates.lng} ${stdCoordinates.lat})` : null,
            } as Address;
          }
          return null;
        });

        const manualAddressResults = await Promise.all(manualAddressPromises);
        const validManualAddresses = manualAddressResults.filter((addr): addr is Address => addr !== null);
        setManualAddresses(validManualAddresses);
      } catch (error) {
        console.error('Error fetching address locations:', error);
      } finally {
        setIsLoading(false);
      }
    }
  }, [jwt, fetchAddressLocation, flyToLocation, resetState, addParcelle]);

  // Handlers
  const handleMapClick = useCallback(async(lat: number, lng: number) => {
    await addParcelle(lat, lng);
  }, [addParcelle]);

  const handleParcelleClick = useCallback(async(parcelleId: string, event: L.LeafletMouseEvent) => {
    event.originalEvent.stopPropagation();
    const parcelleIndex = parcelles.findIndex((p) => p.id === parcelleId);
    if (parcelleIndex !== -1) {
      setParcelles((prev) => prev.filter((p) => p.id !== parcelleId));
      setAddresses((prev) => prev.filter((addr) => addr.parcelleId !== parcelleId));
    } else {
      await addParcelle(event.latlng.lat, event.latlng.lng);
    }
  }, [parcelles, addParcelle]);

  const handleAddressCheck = useCallback((addressId: string) => {
    setAddresses((prev) => prev.map((address) => (address.id === addressId ? { ...address, selected: !address.selected } : address)));
  }, []);

  const handleSelectAll = useCallback(() => {
    setAddresses((prev) => prev.map((address) => ({ ...address, selected: true })));
  }, []);

  const handleDeselectAll = useCallback(() => {
    setAddresses((prev) => prev.map((address) => ({ ...address, selected: false })));
  }, []);

  const handleAddressClick = useCallback((geometry: string, convertFromLambert = true) => {
    const parsedGeometry = parseGeometry(geometry, convertFromLambert);
    if (parsedGeometry) {
      flyToLocation(parsedGeometry.lat, parsedGeometry.lng, 19);
    }
  }, [flyToLocation]);

  const handleSearchSelect = useCallback((result: AutocompleteFeature) => {
    setLastAutocompleteResult(result);
    const [lng, lat] = result.geometry.coordinates;
    flyToLocation(lat, lng, 19);
  }, [flyToLocation]);

  const handleAddManualAddress = useCallback((result: AutocompleteFeature) => {
    const newAddress: Address = {
      id: result.properties.id,
      address: result.properties.name,
      postalCode: result.properties.postcode || '',
      city: result.properties.city || '',
      selected: true,
      geometry: `POINT(${result.geometry.coordinates[0]} ${result.geometry.coordinates[1]})`,
    };
    setManualAddresses((prev) => [...prev, newAddress]);
    setShowManualAddressInput(false);
  }, []);

  const handleDeleteManualAddress = useCallback((addressId: string) => {
    setManualAddresses((prev) => prev.filter((address) => address.id !== addressId));
  }, []);

  const handleUseInSearch = useCallback((address: string) => {
    setSearchQuery(address);
  }, []);

  const handleConfirmSave = useCallback((force = false) => {
    const selectedAddresses = [
      ...addresses.filter((addr) => addr.selected),
      ...manualAddresses.filter((addr) => addr.selected),
    ];

    if (lastAutocompleteResult && !force) {
      const isLastResultSelected = selectedAddresses.some((addr) => addr.id === lastAutocompleteResult.properties.id);
      if (!isLastResultSelected && lastAutocompleteResult.properties.type === 'housenumber') {
        setShowLastResultPopup(true);
        return;
      }
    }
    setIsConfirmationOpen(true);
  }, [addresses, manualAddresses, lastAutocompleteResult]);

  const handleSave = useCallback(async() => {
    if (!selectedCopro) { return; }

    const coproPlots = selectedCopro.plots || [];
    const coproAddresses = selectedCopro.addresses || [];

    setIsConfirmationOpen(false);
    setIsSaving(true);
    try {
      const selectedPlotIds = parcelles.map((parcelle) => parcelle.id);
      const plotsToDelete = coproPlots.filter((plot) => !selectedPlotIds.includes(plot.plot));

      // Delete unselected plots
      await Promise.all(plotsToDelete.map((plot) => fetch(`/api/plots/${plot.id}`, {
        method: 'DELETE',
        headers: { Authorization: JWTAuthorization(jwt) },
      }).then((response) => {
        if (!response.ok) { throw new Error('Failed to delete plot'); }
      })));

      // Save new plots
      const existingPlotIds = coproPlots.map((plot) => plot.plot);
      const newPlots = parcelles.filter((parcelle) => !existingPlotIds.includes(parcelle.id));

      if (newPlots.length > 0) {
        const newPlotPayload = newPlots.map((parcelle) => ({ plot: parcelle.id }));
        const response = await fetch(`/api/copros/${selectedCopro.id}/plots/batch`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: JWTAuthorization(jwt),
          },
          body: JSON.stringify(newPlotPayload),
        });
        if (!response.ok) { throw new Error('Failed to save new plots'); }
      }

      // Handle addresses
      const selectedAddresses = [
        ...addresses.filter((addr) => addr.selected),
        ...manualAddresses.filter((addr) => addr.selected),
      ];
      const addressesToDelete = coproAddresses.filter(
        (existingAddr) => !selectedAddresses.some((selectedAddr) => selectedAddr.id === existingAddr.remoteId),
      );

      // Delete unselected addresses
      await Promise.all(addressesToDelete.map((addr) => fetch(`/api/addresses/${addr.id}`, {
        method: 'DELETE',
        headers: { Authorization: JWTAuthorization(jwt) },
      }).then((response) => {
        if (!response.ok) { throw new Error('Failed to delete address'); }
      })));

      // Add new addresses
      const newAddresses = selectedAddresses.filter(
        (addr) => !coproAddresses.some((existingAddr) => existingAddr.remoteId === addr.id),
      );

      if (newAddresses.length > 0) {
        const newAddressPayload = newAddresses.map((addr) => ({
          postalCode: addr.postalCode,
          address: addr.address,
          city: addr.city,
          remoteId: addr.id,
        }));

        const response = await fetch(`/api/copros/${selectedCopro.id}/addresses/batch`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: JWTAuthorization(jwt),
          },
          body: JSON.stringify(newAddressPayload),
        });

        if (!response.ok) { throw new Error('Failed to add addresses'); }
      }

      showToast('success', 'Adresses et parcelles enregistrées avec succès.');

      try {
        const coproResponse = await fetch(`/api/copros/${selectedCopro.id}`, JWTHeader(jwt));
        if (coproResponse.ok) {
          const updatedCopro = await coproResponse.json();
          onSuccess(updatedCopro);
        } else {
          onSuccess();
        }
      } catch (e) {
        console.error('Error fetching updated copro:', e);
        onSuccess();
      }
    } catch (error) {
      console.error('Error saving addresses and plots:', error);
      showToast('error', "Une erreur est survenue lors de l'enregistrement des adresses et parcelles.");
    } finally {
      setIsSaving(false);
    }
  }, [selectedCopro, parcelles, addresses, manualAddresses, jwt, onSuccess]);

  const handleAddLastResult = useCallback(() => {
    if (lastAutocompleteResult) {
      const isAddressInAddresses = addresses.some((address) => address.id === lastAutocompleteResult.properties.id);
      if (!isAddressInAddresses) {
        handleAddManualAddress(lastAutocompleteResult);
      } else {
        setAddresses((prev) => prev.map((address) => (address.id === lastAutocompleteResult.properties.id ? { ...address, selected: true } : address)));
      }
    }
    setShowLastResultPopup(false);
    handleConfirmSave(true);
  }, [lastAutocompleteResult, addresses, handleAddManualAddress, handleConfirmSave]);

  return {
    // State
    parcelles,
    addresses,
    manualAddresses,
    mapCenter,
    mapZoom,
    addressFound,
    shouldUpdateView,
    isLoading,
    isSaving,
    searchQuery,
    showManualAddressInput,
    lastAutocompleteResult,
    isConfirmationOpen,
    showLastResultPopup,
    mapRef,

    // Setters
    setShouldUpdateView,
    setSearchQuery,
    setShowManualAddressInput,
    setIsConfirmationOpen,
    setShowLastResultPopup,

    // Actions
    handleMapClick,
    handleParcelleClick,
    handleAddressCheck,
    handleSelectAll,
    handleDeselectAll,
    handleAddressClick,
    handleSearchSelect,
    handleAddManualAddress,
    handleDeleteManualAddress,
    handleConfirmSave,
    handleSave,
    handleAddLastResult,
    handleUseInSearch,
    flyToLocation,
    initializeCopro,
    resetState,
  };
}

export default useAddressQualifier;
